import {useState, useCallback} from 'react';

import XLSX from 'xlsx';

type Nullable<T> = {
    [P in keyof T]: T[P] | null;
};

export type ParsedDataExpected = {
    amount: string | number;
    baseAmount: string | number;
    charges: string;
    markingCode: string;
    price: string | number;
    rate: string | number;
    reason: string;
    vat: string | number;
    category: string;
};
export type XlsxFileParsedData = {
    hasErrors: boolean;
    data?: Nullable<ParsedDataExpected[]>;
    invalidRows: XlsxFileInvalidRow;
};

export type XlsxFileInvalidRow = {
    [key: number | string]: {
        error: string[];
    };
};

type ValidateXlsxFileFn = (data: any[]) => XlsxFileInvalidRow;

interface UseParseInvoiceLinesXlsxProps {
    validateXlsxFile?: ValidateXlsxFileFn;
    onDataParsed?: (x: XlsxFileParsedData) => void;
    onError?: (error: string) => void;
}

export const useParseInvoiceLinesXlsx = ({
    validateXlsxFile,
    onDataParsed,
    onError,
}: UseParseInvoiceLinesXlsxProps = {}) => {
    const [isProcessing, setIsProcessing] = useState(false);

    const toCamelCase = (str: string) =>
        str
            .split(' ')
            .map((word, wordIndex) => {
                if (wordIndex === 0) {
                    return word;
                }
                return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join('');

    const parseXlsxFile = useCallback(
        (file: File) => {
            setIsProcessing(true);
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const bstr = e.target?.result;
                    if (!bstr) {
                        onError?.('No data found in the file');
                        setIsProcessing(false);
                        return;
                    }

                    const wb = XLSX.read(bstr, {type: 'binary'});

                    const wsname = wb.SheetNames[0];
                    const ws = wb.Sheets[wsname];

                    const rawData = XLSX.utils.sheet_to_json(ws, {header: 1, defval: ''});

                    if (rawData.length < 3) {
                        console.error('File must have at least 2 header rows and 1 data row');
                        onError?.('File must have at least 2 header rows and 1 data row');
                        setIsProcessing(false);
                        return;
                    }
                    const dictionaryHeaders: Record<string, string> = {
                        'Marking code *': 'markingCode',
                        'VAT category *': 'category',
                        'VAT ( % )*': 'vat',

                        'Marking code *': 'markingCode',
                        'Price *': 'price',
                        'Charge / Allowance': 'charges',
                        'VAT category *': 'category',
                        'VAT ( % )*': 'vat',
                        Reason: 'reason',
                        'Base Amount': 'baseAmount',
                        'rate (%)': 'rate',
                        Amount: 'amount',
                    };
                    const firstHeaderRow = (rawData[0] as string[]).map(
                        (header) =>
                            dictionaryHeaders[header.toString().trim()] || header.toString().trim(),
                    );
                    const secondHeaderRow = (rawData[1] as string[]).map(
                        (header) =>
                            dictionaryHeaders[header.toString().trim()] || header.toString().trim(),
                    );

                    rawData[0] = firstHeaderRow;
                    rawData[1] = secondHeaderRow;

                    const combinedHeaders = firstHeaderRow.map((firstHeader, index) => {
                        const secondHeader = secondHeaderRow[index] || '';

                        const parts = [toCamelCase(firstHeader), toCamelCase(secondHeader)].filter(
                            (part) => part && part.toString().trim(),
                        );

                        return parts.length > 0
                            ? parts.length === 2
                                ? parts[1]
                                : parts.join(' ')
                            : `Column${index + 1}`;
                    });

                    const removeVatCategotyDescription = (category: string) =>
                        category.split('-')[0]?.trim();

                    const removePercent = (value: string) =>
                        (Number(value.replace('%', '')) * 100).toString();

                    const handleRowItem = (header: string, value: string | number) => {
                        const valueString = value.toString();
                        if (header === 'vat' || header === 'rate') {
                            return removePercent(valueString);
                        }
                        if (header === 'category') {
                            return removeVatCategotyDescription(valueString);
                        }
                        return valueString;
                    };

                    const dataRows = rawData.slice(2) as any[][];
                    const data = dataRows
                        .filter((row) => !row.every((cell) => cell === ''))
                        .map((row) => {
                            const rowObject: any = {};
                            combinedHeaders.forEach((header, index) => {
                                rowObject[header] = handleRowItem(header, row[index]) || '';
                            });
                            return rowObject;
                        });

                    if (onDataParsed) {
                        const parsedData: XlsxFileParsedData = {
                            hasErrors: false,
                            invalidRows: {},
                            data,
                        };
                        if (validateXlsxFile) {
                            const invalidRows = validateXlsxFile(data);
                            if (Object.keys(invalidRows).length > 0) {
                                parsedData.hasErrors = true;
                                parsedData.invalidRows = invalidRows;
                                parsedData.data = undefined;
                            }
                        }
                        onDataParsed(parsedData);
                    }
                } catch (error) {
                    console.error('Error parsing XLSX file:', error);
                    onError?.('An error occurred while parsing the file');
                } finally {
                    setIsProcessing(false);
                }
            };

            reader.onerror = () => {
                console.error('Error reading file');
                onError?.('An error occurred while reading the file');
                setIsProcessing(false);
            };

            reader.readAsBinaryString(file);
        },
        [validateXlsxFile, onDataParsed],
    );

    return {
        parseXlsxFile,
        isProcessing,
    };
};
